/* eslint-disable */
'use client';
import {
  useField,
  EditIcon,
  FieldLabel,
  useAllFormFields,
  Button as PayloadButton,
  PlusIcon,
  XIcon,
  useTranslation,
} from '@payloadcms/ui';
import { Canvas } from '@react-three/fiber';
import { type JSONFieldClientComponent } from 'payload';
import { reduceFieldsToValues } from 'payload/shared';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import Draw from '@/components/property-selector/draw';
import { DrawMapObjects } from '@/components/property-selector/draw-components/draw-map-objects';
import { FitBounds } from '@/components/property-selector/draw-components/fit-bounds';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { MapColors, type PatchedProject, type PatchedPropertyUnit, type Point } from '@/types';
import { fetchProjectById } from '@/utilities/models.client';

function MapDialog({
  project,
  propertyUnitId,
  children,
  onSaveShape,
  demarcationShape,
}: {
  propertyUnitId: string;
  demarcationShape: PatchedPropertyUnit['demarcationShape'];
  project: PatchedProject | null;
  readonly children: React.ReactNode;
  onSaveShape?: (shape: Point[]) => void;
}) {
  const [showModalDialog, setShowModalDialog] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [newShape, setNewShape] = useState<Point[] | null>(null);

  const toggleModal = () => {
    setShowModalDialog(!showModalDialog);
  };

  useLayoutEffect(() => {
    let timeout: NodeJS.Timeout;
    if (showModalDialog) {
      timeout = setTimeout(() => {
        setShowMap(true);
      }, 300);
    } else {
      setShowMap(false);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [showModalDialog]);

  let container;
  if (typeof document !== 'undefined') {
    container = document.querySelector('.dialog-portal');
  }

  if (!project) {
    return null;
  }

  return (
    <Dialog
      open={showModalDialog}
      onOpenChange={() => {
        setShowModalDialog(!showModalDialog);
        if (!showModalDialog) {
          setNewShape(null);
        }
      }}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
        container={container}
        className="min-w-[80%] aspect-video"
      >
        <DialogTitle hidden>Draw Property Unit Shape</DialogTitle>
        <div className="w-full h-full max-w-full overflow-hidden flex flex-col">
          <div className="flex-1">
            {showMap && (
              <Draw
                project={project}
                properties={
                  [{ demarcationShape, id: propertyUnitId }] as unknown as PatchedPropertyUnit[]
                }
                onClickSave={(shape) => {
                  setNewShape(shape);
                }}
              />
            )}
          </div>
          <div className="flex py-3 px-4 justify-end">
            <Button
              disabled={newShape == null}
              onClick={() => {
                onSaveShape?.(newShape as Point[]);
                toggleModal();
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// @ts-ignore
const DemarcationEditorField: JSONFieldClientComponent = ({ field, path }) => {
  const { value, setValue } = useField<string>({ path });
  const [fields] = useAllFormFields();
  const formData = reduceFieldsToValues(fields, true);
  const { i18n } = useTranslation();

  const [project, setProject] = useState<PatchedProject | null>(null);

  const {
    mapConfig: { mapImage, hoverColor, availableColor, soldColor },
  } = project ?? {
    mapConfig: {
      hoverColor: MapColors.hover,
      availableColor: MapColors.available,
      soldColor: MapColors.sold,
    },
  };

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        const project = await fetchProjectById(formData.project, i18n.language.slice(0, 2));
        if (mounted) setProject(project);
      } catch (error) {
        console.error('Error fetching project and properties', error);
      }
    };
    if (formData.project) {
      void fetchData();
    }
    return () => {
      mounted = false;
    };
  }, [formData.project, i18n.language]);

  return (
    <div className="field-type">
      <FieldLabel label={field.label} required={field.required} />
      <div className="relative h-[200px]">
        <div className="absolute h-full w-full flex">
          {formData.demarcationShape && (
            <div className="tailwind-app-container h-full aspect-video">
              <Canvas id="draw-canvas" className="w-full auto bg-gray-700" orthographic={true}>
                <ambientLight />
                <pointLight position={[10, 10, 10]} />
                <DrawMapObjects
                  showLegend={false}
                  basemap={mapImage}
                  hoverColor={hoverColor}
                  availableColor={availableColor}
                  soldColor={soldColor}
                  colorsSettings="onlyShowDefaultColor"
                  demarcations={[
                    {
                      ...formData,
                      id: formData.slug,
                    } as PatchedPropertyUnit,
                  ]}
                />
                <FitBounds points={formData.demarcationShape} />
              </Canvas>
            </div>
          )}
          <div
            className={cn('flex flex-col justify-center', {
              'flex-1 bg-gray-200 items-center': !formData.demarcationShape,
              'ml-4 items-start': !!formData.demarcationShape,
            })}
          >
            <MapDialog
              project={project}
              propertyUnitId={formData.slug}
              demarcationShape={formData.demarcationShape}
              onSaveShape={(shape) => {
                setValue(shape);
              }}
            >
              <button className="appearance-none bg-transparent border-0 outline-0 focus:border-0 p-0">
                <PayloadButton
                  icon={value ? <EditIcon /> : <PlusIcon />}
                  iconPosition="left"
                  size="medium"
                  buttonStyle="icon-label"
                  iconStyle="without-border"
                  el="div"
                  className="my-1"
                >
                  {!formData.demarcationShape ? <span>Draw</span> : <span>Edit</span>}
                </PayloadButton>
              </button>
            </MapDialog>

            {formData.demarcationShape && (
              <button
                onClick={() => setValue(null)}
                className="appearance-none bg-transparent border-0 outline-0 focus:border-0 p-0"
              >
                <PayloadButton
                  icon={<XIcon />}
                  iconPosition="left"
                  size="medium"
                  buttonStyle="icon-label"
                  iconStyle="without-border"
                  el="div"
                  className="my-1"
                >
                  <span>Delete</span>
                </PayloadButton>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemarcationEditorField;
